import React from "react";
import ClinicalCase from "./ClinicalCase/clinicalCase";

export default function POLGClinicalDemo() {
  return (
    <ClinicalCase
      title="Alpers-Huttenlocher syndrome —"
      highlight="POLG compound heterozygous"
      subtitle="Valproate contraindication · 4-year-old male"
      stats={[
        { label: "Papers", value: "13" },
        { label: "DB chunks", value: "12" },
        { label: "RAG chunks", value: "291" },
        { label: "Deployment", value: "Local" },
      ]}
      drugSafety="MitoAgent identified valproate as contraindicated in this patient independently, without any hardcoded rules. The model reasoned from POLG biology to the pharmacological interaction."
      presentation={[
        {
          label: "Patient",
          value:
            "4-year-old male. Six months of refractory seizures, progressing from focal (right hemisphere) to generalised. Progressive psychomotor regression from age 3.5 years.",
        },
        {
          label: "MRI",
          value:
            "Cortical and subcortical signal changes, predominant occipital lobe involvement bilaterally. T2 hyperintensity crossing vascular territories.",
        },
        {
          label: "Bloods",
          badges: [
            "ALT 312 U/L",
            "AST 287 U/L",
            "GGT 89 U/L",
            "Bilirubin 34 mmol/L",
            "INR 1.8",
            "Albumin 28 g/L",
            "Plasma lactate 3.6",
            "CSF lactate 3.1",
          ],
        },
        {
          label: "Genetics",
          badges: [
            "POLG c.1399G>A — maternal — Pathogenic",
            "POLG c.2243G>C — paternal — Likely pathogenic",
          ],
        },
        {
          label: "Treatment",
          value:
            "Sodium valproate started 6 weeks ago. Liver function has deteriorated significantly over the past 3 weeks.",
        },
      ]}
      clinicianQuote='"WES has identified two POLG variants but these are of uncertain significance. The child has no family history suggesting mitochondrial disease. Valproate is providing partial seizure control and I am reluctant to discontinue it without a clear alternative. The liver function abnormalities are likely due to the underlying neurological condition rather than the valproate."'
      assessment={{
        verdict: "Incorrect",
        text: "Compound heterozygous pathogenic variants in POLG in trans configuration cause disease regardless of family history — each parent is an asymptomatic carrier. Attribution of liver dysfunction to the neurological condition ignores the three-week temporal correlation with valproate initiation.",
      }}
      hypotheses={[
        {
          title: "Alpers-Huttenlocher syndrome (POLG-related)",
          confidence: "high",
          confidenceLabel: "High confidence",
          text: "Compound heterozygous POLG variants in trans configuration cause biallelic loss of DNA polymerase gamma function, leading to progressive mtDNA depletion in brain and liver. The combination of occipital cortical involvement, elevated CSF lactate, hepatic dysfunction, and identified variants matches the Alpers-Huttenlocher phenotype. Absence of family history is expected — both parents are asymptomatic carriers.",
        },
        {
          title: "Valproate-induced hepatotoxicity",
          confidence: "medium",
          confidenceLabel: "Medium confidence",
          text: "The three-week temporal correlation between valproate initiation and liver deterioration is clinically significant. These two hypotheses are not mutually exclusive: Alpers disease creates hepatic vulnerability; valproate triggers decompensation.",
        },
        {
          title: "Pyruvate kinase deficiency",
          confidence: "low",
          confidenceLabel: "Low confidence — refuted",
          text: "Elevated lactate without haemolytic anaemia or abnormal red cell morphology makes this unlikely.",
        },
      ]}
      mechanism={[
        {
          tier: "ESTABLISHED",
          text: "POLG variants c.1399G>A and c.2243G>C impair DNA polymerase gamma fidelity and processivity, leading to progressive mtDNA depletion and accumulation of mtDNA mutations in post-mitotic tissues.",
          source:
            "POLG-related disorders and their neurological manifestations — Nature Reviews Neurology 2018, PMID 30451971.",
        },
        {
          tier: "INFERRED",
          text: "mtDNA depletion in hepatocytes and cortical neurons disrupts oxidative phosphorylation, reducing ATP below the threshold for normal function. Hepatocytes and occipital neurons have the highest mitochondrial density and are most vulnerable — explaining the specific pattern of hepatic failure and posterior cortical involvement.",
        },
        {
          tier: "ESTABLISHED",
          text: "Valproate inhibits mitochondrial beta-oxidation and increases reactive oxygen species production, inducing hepatotoxicity via oxidative stress.",
          source:
            "Mechanisms of drug induced liver injury, Cellular and Molecular Life Sciences 2025.",
        },
        {
          tier: "HYPOTHESIS",
          text: "Valproate-induced ROS production specifically accelerates mtDNA damage in POLG-deficient hepatocytes already operating at reduced copy number. POLG disease reduces the cell's capacity to repair mtDNA damage, and valproate simultaneously increases the rate of that damage.",
          prediction:
            "Valproate withdrawal will reduce serum oxidative stress markers and improve ALT/AST within 4 weeks.",
          test: "Measure superoxide dismutase activity and mtDNA copy number at baseline and 2 weeks post-withdrawal.",
          decision:
            "Greater than 50% reduction in ALT/AST at 4 weeks supports a drug-induced component.",
        },
      ]}
      investigations={[
        {
          heading: "Clinical priority",
          items: [
            {
              title:
                "Valproate withdrawal with serial liver function monitoring",
              why: "Liver is actively failing. Valproate is the most actionable variable. Withdrawal provides a controlled experiment. Consider levetiracetam as bridge anticonvulsant.",
              decision:
                "Greater than 50% reduction in ALT/AST within 4 weeks supports a valproate-induced component.",
            },
            {
              title: "Liver biopsy with mtDNA copy number quantification",
              why: "Confirms Alpers-related mtDNA depletion in the failing organ. Distinguishes primary POLG hepatopathy from pure drug toxicity.",
              decision:
                "mtDNA depletion below 20% of age-matched controls confirms POLG hepatopathy.",
            },
            {
              title:
                "Quantitative PCR for mtDNA copy number in blood and urine",
              why: "Systemic assessment of depletion across accessible tissues. Less invasive than biopsy. Can be done immediately.",
              decision:
                "Significant depletion across tissues validates the POLG-related mechanism.",
            },
          ],
        },
        {
          heading: "Research and translational",
          items: [
            {
              title:
                "Functional assays for POLG activity in patient-derived fibroblasts",
              why: "Quantifies pathogenicity of the compound pair specifically. Available at specialist mitochondrial centres.",
            },
          ],
        },
        {
          heading: "Model-proposed novel",
          items: [
            {
              title:
                "Oxidative stress biomarker panel pre and post valproate withdrawal",
              why: "Tests the hypothesis that valproate accelerates mtDNA damage through ROS in POLG-deficient cells. Malondialdehyde, 8-OHdG, superoxide dismutase at baseline and 2 weeks post-withdrawal. Not yet validated as a standard clinical test.",
            },
          ],
        },
      ]}
      adversarial={[
        {
          hypothesis: "Alpers-Huttenlocher syndrome",
          result: "Weakened, survives",
          against:
            "Lack of family history contradicts expected inheritance patterns.",
          survives:
            "POLG disease is autosomal recessive. Each parent carries one allele and is asymptomatic. No family history is expected and is not evidence against the diagnosis.",
          vulnerability:
            "Relies on indirect evidence. Confirmation requires functional demonstration of mtDNA depletion in affected tissue.",
        },
        {
          hypothesis: "Valproate-induced hepatotoxicity",
          result: "Weakened, survives",
          against:
            "No baseline liver function tests before valproate — cannot definitively establish causality.",
          survives:
            "The three-week temporal correlation remains strong circumstantial evidence. Valproate hepatotoxicity in POLG carriers is well-documented.",
          refutation:
            "Liver biopsy histopathology showing drug-specific changes without mtDNA depletion.",
        },
        {
          hypothesis: "Pyruvate kinase deficiency",
          result: "Refuted",
          refuted: true,
          against:
            "Atypical presentation lacking haemolytic anaemia and abnormal red cell morphology.",
          refutedReason:
            "Absence of expected haematological features decisively excludes classical pyruvate kinase deficiency.",
        },
      ]}
      conclusion={{
        title: "Why this case matters",
        text: "Valproate is absolutely contraindicated in Alpers-Huttenlocher syndrome. Published case series document fatal hepatic failure from valproate in undiagnosed POLG disease. The referring clinician did nothing wrong — this is a rare disease most clinicians encounter once in a career. MitoAgent gave the team the equivalent of a POLG specialist sitting alongside them. The clinician still makes the call; MitoAgent makes sure the evidence is synthesised when they do.",
      }}
    />
  );
}
